import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadMediaFile } from '@/hooks/useUpload';

// ─────────────────────────────────────────────────────────────
// Édition rapide de l'image d'aperçu (couverture) d'un thème.
// Un bouton par carte ouvre une modale : téléversement d'un fichier
// ou collage d'une URL, aperçu immédiat, enregistrement en base.
// ─────────────────────────────────────────────────────────────

interface ThemeCoverEditorProps {
  themeId: number;
  themeTitle?: string | null;
  currentUrl?: string | null;
  onSaved: (url: string) => void;
}

export default function ThemeCoverEditor({ themeId, themeTitle, currentUrl, onSaved }: ThemeCoverEditorProps) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState(currentUrl || '');
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const openModal = () => {
    setUrl(currentUrl || '');
    setError('');
    setOpen(true);
  };

  const upload = async (file?: File) => {
    if (!file) return;
    setUploading(true);
    setError('');
    try {
      const publicUrl = await uploadMediaFile(file, 'theme-covers');
      setUrl(publicUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'image n'a pas pu être envoyée.");
    } finally {
      setUploading(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const value = url.trim();
      const { error: updErr } = await supabase
        .from('sitewebtheme')
        .update({ imagecouverture: value })
        .eq('id', themeId);
      if (updErr) throw updErr;
      onSaved(value);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : "L'image n'a pas pu être enregistrée.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={openModal}
        title="Changer l'image d'aperçu"
        aria-label="Changer l'image d'aperçu"
        className="absolute bottom-2 right-2 z-10 flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-background-50/95 border border-background-200/70 text-[10px] font-semibold text-foreground-700 hover:bg-background-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
      >
        <i className="ri-image-edit-line text-xs"></i>
        Image d&apos;aperçu
      </button>

      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 bg-black/45" onClick={() => (busy ? undefined : setOpen(false))}></div>
          <div className="relative w-full max-w-md bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70">
              <div className="min-w-0">
                <h3 className="text-sm font-bold text-foreground-950">Image d&apos;aperçu</h3>
                <p className="text-[11px] text-foreground-500 truncate">{themeTitle || 'Thème'}</p>
              </div>
              <button
                type="button"
                onClick={() => (busy ? undefined : setOpen(false))}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 cursor-pointer"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="w-full aspect-[16/10] rounded-lg overflow-hidden bg-background-100 border border-background-200/70 flex items-center justify-center">
                {url ? (
                  <img src={url} alt="Aperçu du thème" className="w-full h-full object-cover object-top" />
                ) : (
                  <div className="flex flex-col items-center gap-1.5 text-foreground-300">
                    <i className="ri-image-line text-3xl"></i>
                    <span className="text-[11px]">Aucune image</span>
                  </div>
                )}
              </div>

              <label className="flex items-center justify-center gap-2 h-11 rounded-lg border border-dashed border-background-300 bg-background-50 text-xs font-semibold text-foreground-600 hover:bg-background-100 cursor-pointer transition-colors">
                {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-cloud-2-line text-base"></i>}
                {uploading ? 'Envoi…' : 'Téléverser une image'}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => void upload(e.target.files?.[0])} />
              </label>

              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Ou coller une URL</label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://…"
                  className="w-full h-11 px-3 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300"
                />
              </div>

              {error && (
                <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 px-5 pb-5">
              <button
                type="button"
                onClick={() => (busy ? undefined : setOpen(false))}
                className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => void save()}
                disabled={busy || uploading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 whitespace-nowrap"
              >
                {busy ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>}
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}